import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Rights } from '@aiinterview/shared';
import { api } from '../api/client';
import { useSession } from '../api/session';
import { Button, Card, TextInput, Loading, ErrorNote, confirmAction, toast } from '../components/ui';
import { DataTable } from '../components/DataTable';
import { Avatar } from '../components/Avatar';

export interface Candidate {
    id: number;
    fullName: string;
    email: string | null;
    mobile: string | null;
    externalRef: string | null;
    position: string | null;
    source: string;
    interviewCount: number;
    /** True when this candidate already has an interview the rules count as open. */
    hasOpenInterview: boolean;
}

export function Candidates() {
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState('');
    const { can } = useSession();
    const queryClient = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: ['candidates', applied],
        queryFn: () => api.get<Candidate[]>(
            `/api/admin/candidates${applied ? `?search=${encodeURIComponent(applied)}` : ''}`)
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/candidates/${id}/delete`),
        onSuccess: async () => {
            toast('Candidate removed.');
            await queryClient.invalidateQueries({ queryKey: ['candidates'] });
        },
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    return (
        <Card
            heading
            title="Candidates"
            lede="Added by hand, imported from CSV, or pushed in by another system through the API."
            // Header holds only what creates a candidate; the search sits with the table it filters.
            action={
                <div className="flex flex-wrap gap-2">
                    {can(Rights.Import) && (
                        <Link to="/candidates/import">
                            <Button type="button" variant="secondary">
                                <i className="bi bi-upload" aria-hidden="true" />
                                Import CSV
                            </Button>
                        </Link>
                    )}
                    {can(Rights.Create) && (
                        <Link to="/candidates/new">
                            <Button type="button">
                                <i className="bi bi-plus-lg" aria-hidden="true" />
                                Add candidate
                            </Button>
                        </Link>
                    )}
                </div>
            }
        >
            <form
                className="mb-4 flex flex-wrap items-center gap-2"
                onSubmit={(e) => { e.preventDefault(); setApplied(search.trim()); }}
            >
                <TextInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Name, email, mobile, reference"
                    className="w-56"
                />
                <Button type="submit" variant="secondary">
                    <i className="bi bi-search" aria-hidden="true" />
                    Search
                </Button>
                {applied && (
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => { setSearch(''); setApplied(''); }}
                    >
                        Clear
                    </Button>
                )}
            </form>

            {error && <ErrorNote message={(error as Error).message} />}

            {isLoading ? <Loading /> : (
                <DataTable
                    rows={data ?? []}
                    rowKey={(row) => row.id}
                    empty={applied ? 'No candidates match that search.' : 'No candidates yet.'}
                    columns={[
                        {
                            header: 'Name',
                            sortBy: (row) => row.fullName,
                            cell: (row) => (
                                <div className="flex items-center gap-2.5">
                                    <Avatar name={row.fullName} />
                                    <Link
                                        to={`/candidates/${row.id}`}
                                        className="font-medium hover:underline"
                                        style={{ color: 'var(--app-heading)' }}
                                    >
                                        {row.fullName}
                                    </Link>
                                </div>
                            )
                        },
                        {
                            header: 'Contact',
                            sortBy: (row) => row.email ?? row.mobile,
                            cell: (row) => (
                                <div>
                                    {row.email && (
                                        <a
                                            href={`mailto:${row.email}`}
                                            className="hover:underline"
                                            style={{ color: 'var(--brand-primary)' }}
                                        >
                                            {row.email}
                                        </a>
                                    )}
                                    {row.mobile && (
                                        <span
                                            className="block text-xs"
                                            style={{ color: 'var(--text-muted-safe)' }}
                                        >
                                            {row.mobile}
                                        </span>
                                    )}
                                    {!row.email && !row.mobile && (
                                        <span style={{ color: 'var(--app-nav-section)' }}>—</span>
                                    )}
                                </div>
                            )
                        },
                        {
                            header: 'Role',
                            sortBy: (row) => row.position,
                            cell: (row) => row.position
                                ?? <span style={{ color: 'var(--app-nav-section)' }}>—</span>
                        },
                        {
                            header: 'Reference',
                            sortBy: (row) => row.externalRef,
                            cell: (row) => row.externalRef
                                ? <span className="app-mono">{row.externalRef}</span>
                                : <span style={{ color: 'var(--app-nav-section)' }}>—</span>
                        },
                        {
                            header: 'Source',
                            sortBy: (row) => row.source,
                            cell: (row) => <strong className="text-xs">{row.source}</strong>
                        },
                        {
                            header: 'Interviews',
                            sortBy: (row) => row.interviewCount,
                            cell: (row) => row.interviewCount > 0
                                ? (
                                    <Link
                                        to={`/candidates/${row.id}/interviews`}
                                        className="hover:underline"
                                        style={{ color: 'var(--brand-primary)' }}
                                    >
                                        {row.interviewCount}
                                    </Link>
                                )
                                : 0
                        },
                        {
                            header: 'Actions',
                            className: 'text-right',
                            cell: (row) => (
                                <div className="flex justify-end gap-2">
                                    {can(Rights.Create) && (
                                        // Disabled rather than hidden when one is already open, and it
                                        // says why. The server refuses this, so offering it would be a
                                        // button that lies — and the same rule decides both answers.
                                        row.hasOpenInterview ? (
                                            <Button
                                                variant="secondary"
                                                disabled
                                                title={`${row.fullName} already has an interview open. `
                                                    + 'The next one can be created once it is approved or rejected.'}
                                            >
                                                <i className="bi bi-calendar-plus" aria-hidden="true" />
                                                Interview
                                            </Button>
                                        ) : (
                                            <Link to={`/interviews/new?candidateId=${row.id}`}>
                                                <Button>
                                                    <i className="bi bi-calendar-plus" aria-hidden="true" />
                                                    Interview
                                                </Button>
                                            </Link>
                                        )
                                    )}

                                    <Link to={`/candidates/${row.id}/interviews`}>
                                        <Button variant="secondary">
                                            <i className="bi bi-link-45deg" aria-hidden="true" />
                                            Links
                                        </Button>
                                    </Link>

                                    <Link to={`/candidates/${row.id}`}>
                                        <Button variant="secondary">
                                            <i className="bi bi-pencil" aria-hidden="true" />
                                            Edit
                                        </Button>
                                    </Link>

                                    {can(Rights.Delete) && (
                                        <Button
                                            variant="danger-quiet"
                                            busy={remove.isPending && remove.variables === row.id}
                                            onClick={async () => {
                                                const confirmed = await confirmAction({
                                                    title: `Remove ${row.fullName}?`,
                                                    text: 'Their interviews and reports are kept.',
                                                    confirmText: 'Remove candidate',
                                                    danger: true
                                                });
                                                if (confirmed) remove.mutate(row.id);
                                            }}
                                        >
                                            <i className="bi bi-trash" aria-hidden="true" />
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            )
                        }
                    ]}
                />
            )}
        </Card>
    );
}
